import { Intent } from "@blueprintjs/core";
import {
  Filter,
  FilterCellBinaryColumnPredicate,
  FilterCellListBinaryColumnPredicate,
  FilterCellLumpColumnPredicate,
  FilterCellUnaryColumnPredicate,
  FilterGroup,
  assertNever,
  filterFullyConfigured,
  tryParseRelativeDateOffsetValue,
} from "@hex/common";
import moment from "moment";
import { lighten } from "polished";
import React from "react";
import styled from "styled-components";

import { HexTag } from "../../../../hex-components/HexTag.js";
import { maybePluralize } from "../../../../util/string.js";

export function getSqlPreviewFromFilterGroup({
  aggregation,
  group,
  inPreview = false,
  isTableDisplayFilter,
  keepRemove = undefined,
  position,
  topLevel,
  truncationLimit = Number.MAX_VALUE,
}: {
  group: FilterGroup | null;
  topLevel: boolean;
  position: number[];
  inPreview?: boolean;
  keepRemove?: string;
  isTableDisplayFilter?: boolean;
  truncationLimit?: number;
  aggregation?: string;
}): JSX.Element {
  if (
    group == null ||
    group.filters.filter(filterFullyConfigured).length === 0
  ) {
    return <></>;
  }

  let index = 0;

  const elements = group.filters
    .filter(filterFullyConfigured)
    .map((filterOrGroup) => {
      if (FilterGroup.guard(filterOrGroup)) {
        return getSqlPreviewFromFilterGroup({
          group: filterOrGroup,
          topLevel: false,
          position: [...position, index++],
          aggregation,
        });
      } else if (Filter.guard(filterOrGroup)) {
        // for longer filters, to make sure only the last element gets truncated, preprocess/inspect the generated sql for each item and
        // if it doesnt need to be truncated then specify that via CSS
        let sql = getSqlFromFilter({
          filter: filterOrGroup,
          position: [...position, index++],
          truncate: false,
          aggregation,
        });
        const sqlText = getTextFromElement(sql);
        if (sqlText.length > truncationLimit) {
          sql = getSqlFromFilter({
            filter: filterOrGroup,
            position: [...position, index++],
            truncationLimit,
            aggregation,
          });
        }
        truncationLimit -= sqlText.length;
        return sql;
      }
    });

  const groupPrefix = topLevel
    ? `${keepRemove != null ? `${keepRemove} ` : ""}where`
    : "";

  if (elements.length > 1) {
    const output = [];

    output.push(elements[0]);

    for (let i = 1; i < elements.length; i++) {
      output.push(
        <div
          key={`${[...position, i].toString()}${group.operation}`}
        >{` ${group.operation.toLowerCase()} `}</div>,
      );
      output.push(elements[i]);
    }
    return !topLevel ? (
      <PreviewFilterGroupDiv
        key={`${position.toString()}group`}
        $inPreview={inPreview}
      >
        {groupPrefix}
        {!isTableDisplayFilter && <StyledParenLeft>(</StyledParenLeft>}
        {output}
        {!isTableDisplayFilter && <StyledParenRight>)</StyledParenRight>}
      </PreviewFilterGroupDiv>
    ) : (
      <PreviewFilterGroupDiv
        key={`${position.toString()}group`}
        $inPreview={inPreview}
      >
        {groupPrefix}
        {output}
      </PreviewFilterGroupDiv>
    );
  } else {
    return (
      <PreviewFilterGroupDiv
        key={`${position.toString()}group`}
        $inPreview={inPreview}
      >
        {groupPrefix}
        {elements}
      </PreviewFilterGroupDiv>
    );
  }
}

export const PreviewColumnContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`;

function formatFullList(items: string[]): string {
  return items.join(", ");
}

export function getSqlFromFilter({
  aggregation,
  filter,
  forTooltip = false,
  intent,
  position,
  truncate = true,
  truncationLimit = Number.MAX_VALUE,
}: {
  filter: Filter;
  position: number[];
  truncationLimit?: number;
  truncate?: boolean;
  aggregation?: string;
  intent?: Intent;
  forTooltip?: boolean;
}): JSX.Element {
  if (truncationLimit <= 0) {
    return <></>;
  }

  // Check truncation against the full text (column + aggregation)
  const fullText = aggregation
    ? `${aggregation} of ${filter.column}`
    : filter.column;
  if (fullText.length > truncationLimit) {
    return (
      <TruncatablePreviewFilterDiv key={position.toString()}>
        <TruncatablePreviewValue>{fullText}</TruncatablePreviewValue>
      </TruncatablePreviewFilterDiv>
    );
  }

  const columnDisplay = (
    <PreviewColumnContainer>
      <PreviewValue>{filter.column}</PreviewValue>
      {aggregation && (
        <HexTag intent={intent} small={true}>
          {aggregation}{" "}
        </HexTag>
      )}
    </PreviewColumnContainer>
  );

  const WrapperDiv = truncate ? TruncatablePreviewFilterDiv : PreviewFilterDiv;

  if (FilterCellUnaryColumnPredicate.guard(filter.operation)) {
    switch (filter.operation.op) {
      case "IS_TRUE":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>=</Operator>
            <TruncatablePreviewValue>True</TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "IS_FALSE":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>=</Operator>
            <TruncatablePreviewValue>False</TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "ALWAYS":
        return <></>;
      case "IS_NULL":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>is</Operator>
            <TruncatablePreviewValue>null</TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "NOT_NULL":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>is not</Operator>
            <TruncatablePreviewValue>null</TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "IS_EMPTY":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>=</Operator>
            <PreviewValue>empty</PreviewValue>
          </WrapperDiv>
        );
      case "NOT_EMPTY":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>!=</Operator>
            <TruncatablePreviewValue>empty</TruncatablePreviewValue>
          </WrapperDiv>
        );
      default:
        assertNever(filter.operation, filter.operation);
    }
  } else if (FilterCellBinaryColumnPredicate.guard(filter.operation)) {
    switch (filter.operation.op) {
      case "GT":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>{">"}</Operator>
            <TruncatablePreviewValue>
              {filter.operation.arg}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "GTE":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>{">="}</Operator>
            <TruncatablePreviewValue>
              {filter.operation.arg}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "EQ":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>=</Operator>
            <TruncatablePreviewValue>
              {filter.operation.arg}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "NEQ":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>!=</Operator>
            <TruncatablePreviewValue>
              {filter.operation.arg}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "LTE":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>{"<="}</Operator>
            <TruncatablePreviewValue>
              {filter.operation.arg}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "LT":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>{"<"}</Operator>
            <TruncatablePreviewValue>
              {filter.operation.arg}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "CONTAINS":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>contains</Operator>
            <TruncatablePreviewValue>
              {filter.operation.arg}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "NOT_CONTAINS":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>doesn&apos;t contain</Operator>
            <TruncatablePreviewValue>
              {filter.operation.arg}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "DATE_BEFORE":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>before</Operator>
            <TruncatablePreviewValue>
              {formatDateArg(filter.operation.arg)}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "DATE_EQUAL":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>=</Operator>
            <TruncatablePreviewValue>
              {formatDateArg(filter.operation.arg)}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "DATE_NOT_EQUAL":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>!=</Operator>
            <TruncatablePreviewValue>
              {formatDateArg(filter.operation.arg)}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "DATE_AFTER":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>after</Operator>
            <TruncatablePreviewValue>
              {formatDateArg(filter.operation.arg)}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "DATE_EQUAL_OR_AFTER":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>on or after</Operator>
            <TruncatablePreviewValue>
              {formatDateArg(filter.operation.arg)}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "DATE_EQUAL_OR_BEFORE":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>on or before</Operator>
            <TruncatablePreviewValue>
              {formatDateArg(filter.operation.arg)}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      default:
        assertNever(filter.operation, filter.operation);
    }
  } else if (FilterCellListBinaryColumnPredicate.guard(filter.operation)) {
    switch (filter.operation.op) {
      case "IS_ONE_OF":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>is one of</Operator>
            <TruncatablePreviewValue
              key={`${position.toString()}${filter.operation.arg}`}
            >
              {forTooltip
                ? formatFullList(filter.operation.arg)
                : formatLimitedList(filter.operation.arg)}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "NOT_ONE_OF":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>is not one of</Operator>
            <TruncatablePreviewValue
              key={`${position.toString()}${filter.operation.arg}`}
            >
              {forTooltip
                ? formatFullList(filter.operation.arg)
                : formatLimitedList(filter.operation.arg)}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "DATE_BETWEEN":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>in</Operator>
            <TruncatablePreviewValue>
              {formatDateRangeArgs(
                filter.operation.arg[0],
                filter.operation.arg[1],
              )}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      default:
        assertNever(filter.operation, filter.operation);
    }
  } else if (FilterCellLumpColumnPredicate.guard(filter.operation)) {
    switch (filter.operation.op) {
      case "TOP_N":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>is in</Operator>
            <TruncatablePreviewValue>
              Top {filter.operation.arg}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "BOTTOM_N":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>is in</Operator>
            <TruncatablePreviewValue>
              Bottom {filter.operation.arg}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "NOT_TOP_N":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>is not in</Operator>
            <TruncatablePreviewValue>
              Top {filter.operation.arg}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      case "NOT_BOTTOM_N":
        return (
          <WrapperDiv key={position.toString()}>
            {columnDisplay}
            <Operator>is not in</Operator>
            <TruncatablePreviewValue>
              Bottom {filter.operation.arg}
            </TruncatablePreviewValue>
          </WrapperDiv>
        );
      default:
        assertNever(filter.operation, filter.operation);
    }
  } else {
    assertNever(filter.operation, filter.operation);
  }
}

export const Operator = styled.span``;

export const PreviewFilterGroupDiv = styled.div<{ $inPreview: boolean }>`
  display: flex;
  flex-direction: row;

  flex-wrap: ${({ $inPreview }) => ($inPreview ? "nowrap" : "wrap")};
  gap: 4px;
  align-items: center;

  color: ${({ theme }) => lighten(0.05, theme.fontColor.MUTED)};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PreviewFilterDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  white-space: nowrap;

  max-width: 300px;
`;

export const TruncatablePreviewFilterDiv = styled(PreviewFilterDiv)`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledParen = styled.span`
  margin-top: 0px;

  font-size: ${({ theme }) => theme.fontSize.DEFAULT};
`;

const StyledParenLeft = styled(StyledParen)`
  margin-right: -2px;
`;

const StyledParenRight = styled(StyledParen)`
  margin-left: -2px;
`;

export const PreviewValue = styled.div`
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};

  color: ${({ theme }) => theme.fontColor.DEFAULT};
`;

export const TruncatablePreviewValue = styled(PreviewValue)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const DEFAULT_TRUNCATION_LIMIT = 2;

function formatLimitedList(items: string[]): string {
  const maxItems = DEFAULT_TRUNCATION_LIMIT;
  if (items.length <= maxItems) {
    return items.join(", ");
  } else {
    const displayedItems = items.slice(0, maxItems);
    const remainingCount = items.length - maxItems;
    return `${displayedItems.join(", ")}, +${remainingCount}`;
  }
}

function formatDateArg(arg: string, useDisplayName = true): string | undefined {
  if (moment(arg).isValid() || arg === "") {
    return arg;
  }
  const relativeDate = tryParseRelativeDateOffsetValue(arg);

  if (relativeDate == null) {
    return "";
  }

  if (!useDisplayName || relativeDate.displayName == null) {
    return `${relativeDate.amount} ${maybePluralize(
      relativeDate.amount,
      relativeDate.unit.toLowerCase(),
      relativeDate.unit.toLowerCase() + "s",
    )} ${relativeDate.operation === "AGO" ? "ago" : "from now"}`;
  } else {
    return relativeDate.displayName;
  }
}

function formatDateRangeArgs(start: string, end: string): string {
  const relativeStartDate = tryParseRelativeDateOffsetValue(start);
  const relativeEndDate = tryParseRelativeDateOffsetValue(end);

  if (
    relativeStartDate == null ||
    relativeEndDate == null ||
    relativeEndDate.amount !== 0
  ) {
    return `${formatDateArg(start, false)} - ${formatDateArg(end)}`;
  }

  // special case for <relative date> to today
  return `Past${relativeStartDate.amount === 1 ? "" : ` ${relativeStartDate.amount}`} ${maybePluralize(
    relativeStartDate.amount,
    relativeStartDate.unit.toLowerCase(),
    relativeStartDate.unit.toLowerCase() + "s",
  )}`;
}

export function getTextFromElement(element: JSX.Element): string {
  if (typeof element === "string") {
    return element;
  } else if (element instanceof Array) {
    return element.map(getTextFromElement).join(" ");
  } else if (typeof element === "object" && element) {
    return getTextFromElement(element.props.children);
  } else {
    return "";
  }
}
